import React, { useState } from "react";
import { api } from "../../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Phone, Mail, Clock, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { COMPANY } from "../../data/content";

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    if (errors[field]) setErrors({ ...errors, [field]: null });
  };

  const validate = () => {
    const errs = {};
    if (!form.name || form.name.trim().length < 2) errs.name = "Please enter your full name.";
    if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Please enter a valid email address.";
    if (!form.message || form.message.trim().length < 5) errs.message = "Please enter a message (at least 5 characters).";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="section-anchor py-16 sm:py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--brand-navy)]">
            Still Have Questions?
          </h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            Send us a message and our team will get back to you as soon as possible.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <Card className="lg:col-span-7 border bg-white">
            <CardContent className="p-6 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="contact-name">Full Name *</Label>
                    <Input
                      id="contact-name"
                      placeholder="Your full name"
                      value={form.name}
                      onChange={update("name")}
                      data-testid="contact-name-input"
                    />
                    {errors.name && <p className="text-xs text-destructive" aria-live="polite">{errors.name}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="contact-email">Email Address *</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={update("email")}
                      data-testid="contact-email-input"
                    />
                    {errors.email && <p className="text-xs text-destructive" aria-live="polite">{errors.email}</p>}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact-phone">Phone Number</Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    placeholder="345-XXX-XXXX"
                    value={form.phone}
                    onChange={update("phone")}
                    data-testid="contact-phone-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact-message">Message *</Label>
                  <Textarea
                    id="contact-message"
                    placeholder="How can we help?"
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    data-testid="contact-message-input"
                  />
                  {errors.message && <p className="text-xs text-destructive" aria-live="polite">{errors.message}</p>}
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[color:var(--brand-navy)] hover:bg-[color:var(--brand-navy-2)] h-11 px-8 btn-lift"
                  data-testid="contact-form-submit-button"
                >
                  {loading ? (
                    <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Sending...</span>
                  ) : (
                    <span className="inline-flex items-center gap-2"><Send className="h-4 w-4" /> Send Message</span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="lg:col-span-5">
            <Card className="border bg-[color:var(--brand-navy)] text-white h-full">
              <CardContent className="p-6 sm:p-8">
                <h3 className="font-heading font-semibold text-lg">Visit or Call Us</h3>
                <div className="mt-6 space-y-5">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-[color:var(--brand-sky)] shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Our Location</p>
                      <p className="text-sm text-white/70 mt-1 leading-relaxed">{COMPANY.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-[color:var(--brand-sky)] shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Phone</p>
                      <a href={COMPANY.phoneHref} className="text-sm text-white/70 hover:text-white hover:underline mt-1 inline-block">
                        {COMPANY.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-[color:var(--brand-sky)] shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Email</p>
                      <a href={COMPANY.emailHref} className="text-sm text-white/70 hover:text-white hover:underline mt-1 inline-block">
                        {COMPANY.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-[color:var(--brand-sky)] shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Business Hours</p>
                      <p className="text-sm text-white/70 mt-1">{COMPANY.hours}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
